import { Button, Card, CardHeaderActions, CardHeading, Empty, Icon, IconButton, Id, Trigger } from "@dgs/core";
import { FC, useId } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { GuestSelfTravelResource } from "../../../../../shared/types/selfTravel";
import { GuestTravelInquiry } from "../../../../../shared/types/travelInquiry";
import { TravelSectionSettingsResource } from "../../../shared/types";
import { GuestTravelFormValues } from "../../../shared/types/travel";
import {
	TravelDirection,
	TravelSegmentFormValues,
	TravelSegmentIndexResource,
	isTravelSegmentIndexResource,
} from "../../../shared/types/travelSegment";
import { TravelTemplateIndexResource } from "../../../shared/types/travelTemplate";
import { TravelDrawer } from "./drawer/TravelDrawer";
import { SelfTravelCard } from "./segmentCard/SelfTravelCard";
import { TravelInquiryCard } from "./segmentCard/TravelInquiryCard";
import { TravelSegmentCard } from "./segmentCard/TravelSegmentCard";
import { travelSegmentIdentifier } from "./travelSegmentIdentifier";

const ArrivalDepartureCard = styled(Card)<{ $hasError?: boolean }>`
	display: grid;
	background: ${({ theme }) => theme.colors.palette.grey[10]};
	grid-template:
		"arrivalHeader" max-content
		"arrivalList" 1fr
		"departureHeader" max-content
		"departureList" 1fr
		"footer" max-content
		"errors" / 1fr;

	${({ theme }) => theme.breakpoints.largeDesktop} {
		grid-template:
			"arrivalHeader departureHeader" max-content
			"arrivalList departureList" 1fr
			"footer footer" max-content
			"errors errors" max-content / 1fr 1fr;
	}

	${({ theme, $hasError }) =>
		$hasError &&
		css`
			border-color: ${theme.colors.palette.danger.main.base};
		`}
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(6)};

	${({ theme }) => theme.breakpoints.largeDesktop} {
		border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	}
`;

const ArrivalHeader = styled(Header)`
	grid-area: arrivalHeader;

	${({ theme }) => theme.breakpoints.largeDesktop} {
		border-right: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	}
`;

const DepartureHeader = styled(Header)`
	grid-area: departureHeader;
`;

const List = styled.ul`
	display: flex;
	flex-direction: column;
	list-style: none;
	gap: ${({ theme }) => theme.spacing(2)};
	padding: ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(6)};
	min-height: 500px;
`;

const ArrivalList = styled(List)`
	grid-area: arrivalList;

	${({ theme }) => theme.breakpoints.largeDesktop} {
		border-right: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	}
`;

const DepartureList = styled(List)`
	grid-area: departureList;
`;

const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	grid-area: footer;

	border-top: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};

	padding: ${({ theme }) => theme.spacing(4)};
`;

const Errors = styled.ul`
	list-style: none;
	grid-area: errors;
	background: ${({ theme }) => theme.colors.palette.danger.main.base};
	color: ${({ theme }) => theme.colors.palette.danger.main.contrast};
	padding: ${({ theme }) => theme.spacing(2)};
`;

const TravelHeading = styled.h2`
	color: ${({ theme }) => theme.colors.palette.grey[90]};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
	font-size: ${({ theme }) => theme.fonts.sizes.large};
	padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

interface Props {
	registrationFormId: Id;
	heading: string;
	travel: GuestTravelFormValues;
	errors?: string[] | false;
	onDelete: () => void;
	onRemoveTravelSegment: (
		direction: TravelDirection,
		segment: TravelSegmentIndexResource | TravelSegmentFormValues,
	) => void;
	onAddTravelSegments: (
		direction: TravelDirection,
		selection: (TravelSegmentIndexResource | TravelSegmentFormValues)[],
	) => void;
	onChangeTravelInquiry: (direction: TravelDirection, inquiry: GuestTravelInquiry) => void;
	onRemoveTravelInquiry: (direction: TravelDirection) => void;
	onChangeSelfTravel: (direction: TravelDirection, selfTravel: GuestSelfTravelResource) => void;
	onRemoveSelfTravel: (direction: TravelDirection) => void;
	onSelectTravelTemplate: (direction: TravelDirection, travelTemplate: TravelTemplateIndexResource) => void;
	settings: TravelSectionSettingsResource;
}

export const Travel: FC<Props> = ({
	registrationFormId,
	heading,
	travel,
	onRemoveTravelSegment,
	onAddTravelSegments,
	onDelete,
	errors,
	onChangeTravelInquiry,
	onRemoveTravelInquiry,
	onChangeSelfTravel,
	onRemoveSelfTravel,
	onSelectTravelTemplate,
	settings,
}) => {
	const { t } = useTranslation();
	const travelId = useId();
	const errorId = useId();

	return (
		<section aria-labelledby={travelId} aria-invalid={!!errors} aria-errormessage={errors ? errorId : undefined}>
			<TravelHeading id={travelId}>{heading}</TravelHeading>
			<ArrivalDepartureCard $hasError={!!errors} $variant="outlined">
				<ArrivalHeader>
					<CardHeading>{t("Arrival")}</CardHeading>
					<Trigger
						render={(props) => (
							<TravelDrawer
								open={props.open}
								onClose={props.handleClose}
								registrationFormId={registrationFormId}
								direction="arrival"
								heading={t("Arrival")}
								initialSelection={travel.arrivals}
								inquiry={travel.arrivalInquiry}
								selfTravel={travel.arrivalSelfTravel}
								onAddTravelSegments={(selection) => onAddTravelSegments("arrival", selection)}
								onAddTravelInquiry={(selection) => onChangeTravelInquiry("arrival", selection)}
								onAddSelfTravel={(selfTravel) => onChangeSelfTravel("arrival", selfTravel)}
								onSelectTravelTemplate={(travelTemplate) => onSelectTravelTemplate("arrival", travelTemplate)}
								settings={settings}
							/>
						)}
					>
						{(triggerProps) => (
							<IconButton
								{...triggerProps}
								type="button"
								title={t("Add arrival segment")}
								icon="plus"
								color="primary"
							/>
						)}
					</Trigger>
				</ArrivalHeader>
				<ArrivalList aria-label={t("Arrival")}>
					{!travel.arrivals.length &&
						!travel.arrivalInquiry &&
						!travel.arrivalSelfTravel &&
						!travel.arrivalTravelTemplate && <Empty>{t("No travel segments have been added yet.")}</Empty>}
					{travel.arrivalInquiry && (
						<li aria-label={`${t("Inquiry")}: ${t(travel.arrivalInquiry.type)}`}>
							<TravelInquiryCard
								travelInquiry={travel.arrivalInquiry}
								onRemove={() => onRemoveTravelInquiry("arrival")}
								handleEditInquiry={(values) => onChangeTravelInquiry("arrival", values)}
							/>
						</li>
					)}
					{travel.arrivalSelfTravel && (
						<li aria-label={`${t("Self travel")}`}>
							<SelfTravelCard
								selfTravel={travel.arrivalSelfTravel}
								onRemove={() => onRemoveSelfTravel("arrival")}
								onEditSelfTravel={(values) => onChangeSelfTravel("arrival", values)}
								isRemarksFieldRequired={settings.isRemarksFieldRequired}
								direction="arrival"
							/>
						</li>
					)}
					{travel.arrivalTravelTemplate?.travelSegments.map((segment, index) => (
						<li key={segment.id} aria-label={`${t(segment.type)} ${travelSegmentIdentifier(segment)}`}>
							<TravelSegmentCard
								direction="arrival"
								travelSegment={segment}
								heading={`${index + 1}. ${t(segment.type)}`}
							/>
						</li>
					))}
					{travel.arrivals.map((segment, index) => (
						<li
							key={isTravelSegmentIndexResource(segment) ? segment.id : `form-value ${index}`}
							aria-label={`${t(segment.type)} ${travelSegmentIdentifier(segment)}`}
						>
							<TravelSegmentCard
								direction="arrival"
								travelSegment={segment}
								heading={`${index + 1}. ${t(segment.type)}`}
								onRemove={() => onRemoveTravelSegment("arrival", segment)}
							/>
						</li>
					))}
				</ArrivalList>
				<DepartureHeader>
					<CardHeading>{t("Departure")}</CardHeading>
					<CardHeaderActions>
						<Trigger
							render={(props) => (
								<TravelDrawer
									open={props.open}
									onClose={props.handleClose}
									registrationFormId={registrationFormId}
									direction="departure"
									heading={t("Departure")}
									initialSelection={travel.departures}
									inquiry={travel.departureInquiry}
									selfTravel={travel.departureSelfTravel}
									onAddTravelSegments={(selection) => onAddTravelSegments("departure", selection)}
									onAddTravelInquiry={(selection) => onChangeTravelInquiry("departure", selection)}
									onAddSelfTravel={(selfTravel) => onChangeSelfTravel("departure", selfTravel)}
									settings={settings}
									onSelectTravelTemplate={(travelTemplate) => onSelectTravelTemplate("departure", travelTemplate)}
								/>
							)}
						>
							{(triggerProps) => (
								<IconButton
									{...triggerProps}
									type="button"
									title={t("Add departure segment")}
									icon="plus"
									color="primary"
								/>
							)}
						</Trigger>
					</CardHeaderActions>
				</DepartureHeader>
				<DepartureList aria-label={t("Departure")}>
					{!travel.departures.length &&
						!travel.departureInquiry &&
						!travel.departureSelfTravel &&
						!travel.departureTravelTemplate && <Empty>{t("No travel segments have been added yet.")}</Empty>}
					{travel.departureInquiry && (
						<li aria-label={`${t("Inquiry")}: ${t(travel.departureInquiry.type)}`}>
							<TravelInquiryCard
								travelInquiry={travel.departureInquiry}
								onRemove={() => onRemoveTravelInquiry("departure")}
								handleEditInquiry={(values) => onChangeTravelInquiry("departure", values)}
							/>
						</li>
					)}
					{travel.departureTravelTemplate?.travelSegments.map((segment, index) => (
						<li key={segment.id} aria-label={`${t(segment.type)} ${travelSegmentIdentifier(segment)}`}>
							<TravelSegmentCard
								direction="departure"
								travelSegment={segment}
								heading={`${index + 1}. ${t(segment.type)}`}
							/>
						</li>
					))}
					{travel.departureSelfTravel && (
						<li aria-label={`${t("Self travel")}`}>
							<SelfTravelCard
								selfTravel={travel.departureSelfTravel}
								onRemove={() => onRemoveSelfTravel("departure")}
								onEditSelfTravel={(values) => onChangeSelfTravel("departure", values)}
								isRemarksFieldRequired={settings.isRemarksFieldRequired}
								direction="departure"
							/>
						</li>
					)}

					{travel.departures.map((segment, index) => (
						<li
							key={isTravelSegmentIndexResource(segment) ? segment.id : `form-value ${index}`}
							aria-label={`${t(segment.type)} ${travelSegmentIdentifier(segment)}`}
						>
							<TravelSegmentCard
								direction="departure"
								travelSegment={segment}
								heading={`${index + 1}. ${t(segment.type)}`}
								onRemove={() => onRemoveTravelSegment("departure", segment)}
							/>
						</li>
					))}
				</DepartureList>
				<Footer>
					<Button onClick={onDelete} type="button" color="danger" prefix={<Icon $icon="delete" />}>
						{t("Delete arrival and departure")}
					</Button>
				</Footer>
				{errors && (
					<Errors id={errorId}>
						{errors.map((error, index) => (
							<li key={index}>{error}</li>
						))}
					</Errors>
				)}
			</ArrivalDepartureCard>
		</section>
	);
};
